// MAP HEIGHTS (rearch-r8k "client one ground"): the 2D Map's heights — its hillshade (maptiles.js
// MapClass -> mapclass.js mapCore 'world') and its elevation readout (main.js mapSrc elevAt) — read
// off GROUND-M, the ground the client draws and the server stands on; no terrain level.
//
//   level   ONE level of groundm/index.json: the coarsest whose `tol` <= cell / 4 (cell 16 m:
//           tol <= 4 m; read from index.levels, never a fixed k — L4 on the live world, 28
//           in-frame nodes, ~0.5 MB br). Only nodes meeting the frame (lookup.js offFrame: the
//           far tree's `outer` nodes are never fetched).
//   fetch   groundm/L<k>/<tx>_<ty>.bin, the asset queue class `map` (MAP FIRST, like the
//           CoarseHeightfield it replaces: before any bulk family); the same URLs GroundTree asks
//           for, so the HTTP / service-worker cache answers the second read. No WebAssembly.
//   posts   { a Float32Array w × h, w, h, cell }: post (i, k) at ((i + 0.5) cell, (k + 0.5) cell)
//           in game metres over the frame (x 0..bbox e1 - e0, z 0..n1 - n0, row 0 = the north
//           edge) = lookup.js groundAt on the decoded node that owns it (node (k, tx, ty) owns x in
//           [tx side + 0.25, (tx + 1) side + 0.25), same in z) — the canonical sampler, no other.
//           A post with no node (a failed fetch) or a NaN (a bore opening's hole) takes the row's
//           last value; the first of a row with none, the previous row's (maptiles._posts' rule
//           for a missing terrain tile); none at all: 0.
//   where   the main thread, a node at a time as it lands (decode = codec.js meshOf, a few ms),
//           sched.js slice() between rows: the posts are wanted HERE (heightAt) and the map worker
//           neither fetches nor shares this module — a hop would only copy them back.
//   heightAt(x, z)  the NEAREST post's height (no interpolating sampler of its own); null before
//           loaded / without posts / off the frame.
//   loaded  resolves when the posts are in (or failed: posts null, logged once).
import { fetchAsset } from './assetq.js';
import { slice } from './sched.js';
import { decodeNode, meshOf } from '../../groundm/codec.js';
import { indexNode, groundAt, nodeSide, offFrame } from '../../groundm/lookup.js';

export const MAP_POST_M = 16;          // the Map's post (m): the hillshade's 16 m posts

// index (groundm/index.json) -> the level the Map reads at post `cell`: the coarsest whose tol
// <= cell / 4 (null: none)
export function mapLevel(index, cell = MAP_POST_M) {
  let best = null;
  for (const L of (index && index.levels) || []) if (L.tol <= cell / 4 && (!best || L.k > best.k)) best = L;
  return best;
}

export class MapHeights {
  // meta: the bake meta (bbox); index: a promise of the parsed groundm/index.json (GroundTree
  // indexRaw: no second fetch) or the parsed index itself
  constructor({ meta, baseUrl, index, cell = MAP_POST_M }) {
    this.baseUrl = baseUrl;
    this.cell = cell;
    const bb = meta.bbox;
    this.extentX = bb.e1 - bb.e0;
    this.extentZ = bb.n1 - bb.n0;
    this.level = null;             // the index level row read
    this.posts = null;             // { a, w, h, cell } once loaded
    this.stats = { k: -1, nodes: 0, failed: 0, holes: 0 };
    this.loaded = this._load(index).catch((e) => { this.posts = null; console.warn('[mapheights] no posts:', e && e.message ? e.message : e); });
  }

  async _load(index) {
    const ix = await index;
    if (!(ix && ix.levels && ix.nodes)) throw new Error('groundm/index.json: no levels / nodes');
    const L = mapLevel(ix, this.cell);
    if (!L) throw new Error(`groundm/index.json: no level with tol <= ${this.cell / 4} m`);
    this.level = L;
    const cell = this.cell, k = L.k, side = nodeSide(k), world = { w: this.extentX, h: this.extentZ };
    const w = Math.ceil(world.w / cell), h = Math.ceil(world.h / cell);
    const a = new Float32Array(w * h).fill(NaN);
    const nodes = ix.nodes.filter((n) => n.k === k && !offFrame(n.tx, n.ty, side, world));
    const S = this.stats;
    S.k = k;
    // every node's fetch at once (the queue orders them); each sampled as it lands, one at a
    // time (a failed one: its posts stay NaN -> the carry)
    let chain = Promise.resolve();
    await Promise.all(nodes.map((n) => fetchAsset(`${this.baseUrl}/groundm/L${n.k}/${n.tx}_${n.ty}.bin`, { prio: 'map', as: 'arrayBuffer' })
      .then((bin) => { const p = chain.then(() => this._sample(a, w, h, n, bin)); chain = p.catch(() => {}); return p; })
      .catch(() => { S.failed++; })));
    // the carry: no node / a hole -> the row's last value, the first of a row the previous row's
    let last = 0;
    for (let r = 0; r < h; r++) {
      for (let i = r * w, e = i + w; i < e; i++) {
        if (a[i] === a[i]) last = a[i];
        else { a[i] = last; S.holes++; }
      }
      if ((r & 63) === 63) await slice();
    }
    this.posts = { a, w, h, cell };
  }

  // one node's posts: the ones it owns (x in [tx side + 0.25, (tx + 1) side + 0.25)) = groundAt
  async _sample(a, w, h, n, bin) {
    if (!bin) throw new Error('no bytes');
    const cell = this.cell, side = nodeSide(n.k), ox = n.tx * side, oz = n.ty * side;
    const m = indexNode({ ...meshOf(decodeNode(new Uint8Array(bin))), level: n.k });
    const own = (o) => Math.ceil((o + 0.25) / cell - 0.5);
    const i0 = Math.max(0, own(ox)), i1 = Math.min(w, own(ox + side)), k0 = Math.max(0, own(oz)), k1 = Math.min(h, own(oz + side));
    for (let r = k0; r < k1; r++) {
      const lz = (r + 0.5) * cell - oz;
      for (let i = i0; i < i1; i++) a[r * w + i] = groundAt(m, (i + 0.5) * cell - ox, lz);
      await slice();
    }
    this.stats.nodes++;
  }

  // the nearest post's height (m) | null (not loaded / off the frame)
  heightAt(x, z) {
    const P = this.posts;
    if (!P || !(x >= 0 && z >= 0 && x <= P.w * P.cell && z <= P.h * P.cell)) return null;
    const i = Math.min(P.w - 1, Math.floor(x / P.cell)), k = Math.min(P.h - 1, Math.floor(z / P.cell));
    return P.a[k * P.w + i];
  }
}
